import { useContext } from 'react';
import { DetailContext, UserContext, useEventCallback } from '@eas/common-web';
import { hasSharedEsFilePermission } from '@modules/es-file/es-file-permission';
import { Decision, Me } from '@models';
import { Role } from '@enums';
import { getDecisionEsFileUiElement } from './decision-es-file-access-map';
import {
  DecisionEsFileAccessLevel,
  DecisionEsFileElement,
} from './decision-es-file-access-map';

export type { DecisionEsFileElement } from './decision-es-file-access-map';

export function useDecisionEsFilePermission(decision?: Decision | null) {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const { isExisting } = useContext(DetailContext);

  const getAccessLevel = useEventCallback((element: DecisionEsFileElement) => {
    const uiElement = getDecisionEsFileUiElement(
      decision?.esFile?.agenda,
      element
    );

    if (!uiElement) {
      return 'none' as DecisionEsFileAccessLevel;
    }

    return hasSharedEsFilePermission({
      esFile: decision?.esFile,
      user,
      isExisting,
      element: uiElement,
      skipAssignmentRoles: [
        'proceedingSubjects',
        'subjectIndexes',
        'regulations',
      ].includes(element)
        ? [Role.ANALYST]
        : undefined,
    })
      ? ('write' as DecisionEsFileAccessLevel)
      : ('none' as DecisionEsFileAccessLevel);
  });

  const canRead = useEventCallback((element: DecisionEsFileElement) => {
    return getAccessLevel(element) === 'write';
  });

  const canWrite = useEventCallback((element: DecisionEsFileElement) => {
    return getAccessLevel(element) === 'write';
  });

  const hasPermission = useEventCallback((element: DecisionEsFileElement) => {
    return canWrite(element);
  });

  return {
    canRead,
    canWrite,
    hasPermission,
    getAccessLevel,
  };
}
